import {
  PropsWithChildren,
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Account } from "@services/account/types";
import { useErrorNotification } from "@common/components/Notification";
import { getAccount } from "@services/account";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";

export const AccountDetailContext = createContext<undefined | Account>(
  undefined
);

export const AccountDetailDispatchContext = createContext<{
  refetch: (fullRefresh?: boolean) => Promise<Account | undefined>;
}>({
  refetch: async () => {
    return undefined;
  },
});

export const AccountDetailProvider = ({ children }: PropsWithChildren<{}>) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [account, setAccount] = useState<Account>();
  const { accountid } = useParams();
  const dispatchError = useErrorNotification();
  const [, , , , , , updateAccount] = useUserSelectedAccounts();

  const fetch = async (accountId: string, fullRefresh: boolean) => {
    try {
      let updatedAccount = await getAccount(accountId);
      setAccount(updatedAccount);
      if (fullRefresh) updateAccount(updatedAccount);
      return updatedAccount;
    } catch (e) {
      navigate("/");
      dispatchError(e, { resourceName: t("Account") });
    }
    return;
  };

  const refetch = useCallback(
    async (fullRefresh: boolean = false) => {
      if (fullRefresh) setAccount(undefined);
      if (accountid !== undefined) return await fetch(accountid, fullRefresh);
      return;
    },
    [accountid]
  );

  useEffect(() => {
    refetch();
  }, [accountid]);

  return (
    <AccountDetailContext.Provider value={account}>
      <AccountDetailDispatchContext.Provider value={{ refetch }}>
        {children}
      </AccountDetailDispatchContext.Provider>
    </AccountDetailContext.Provider>
  );
};
